import i from 'i18next'
import { useEffect } from 'react'
import Head from 'next/head'
import Error from 'next/error'
import { useRouter } from 'next/router'
import useTranslations, { getTranslations } from 'lib/hooks/use-translations'
import {
	getCollectionProducts,
	CollectionProducts,
	getMenuCollections,
	MenuCollection,
	getMenu,
	MenuLink,
} from 'lib/hooks/use-collections'
import Layout from 'components/Layout'
import Loading from 'components/product/Loading'
import Thumbnail from 'components/product/Thumbnail'
import CollectionSection from 'components/sections/Collection'
import Footer from 'components/sections/Footer'
import { motion } from 'framer-motion'
import { H1 } from 'components/sections/Header'
import { getTitle } from 'components/collection/Title'
import { products as ease } from 'constants/easings'
import { res } from 'components/Image'
import { addRating } from 'lib/hooks/use-reviews'
import classnames from 'classnames'
import { useWindowSize } from 'lib/hooks/use-window'
import * as Sentry from '@sentry/nextjs'
import { cvgTrack, parseCollection } from 'components/analytics/Converge'
import useStorefront from 'lib/hooks/use-storefront'

export async function getStaticPaths() {
	const collections = await getMenuCollections( false )
	return {
		paths: collections.map( collection => ({
			params: { collection: collection.handle },
		}) ),
		fallback: true,
	}
}

export async function getStaticProps({ params: { collection: handle } }) {
	const translations = await getTranslations({ page: 'collection' })
	const nav = await getMenu()
	const collections = await getMenuCollections()
	const collection = await getCollectionProducts({ handle })

	if ( !collection ) {
		// Query redirects
		const client = useStorefront()
		const { urlRedirects } = await client.request( `{
				urlRedirects(first: 1, query:"\\"collections/${handle}\\"") {
					edges {
						node {
							path
							target
						}
					}
				}
			}` )

		if ( urlRedirects.edges.length ) {
			const { target } = urlRedirects.edges[ 0 ].node
			return {
				redirect: {
					destination: target.replace( '/collections/', '/products/' ),
					permanent: false,
				},
			}
		}
	}

	if ( collection?.products ) await addRating( collection.products )

	return {
		props: { handle, translations, nav, collections, collection },
		revalidate: 600,
	}
}

type Props = {
	handle: string
	loaded: boolean
	translations: any
	nav: MenuLink[]
	collections: MenuCollection[]
	collection: CollectionProducts
}

export default function Collection({
	handle,
	loaded,
	translations,
	nav,
	collections,
	collection,
}: Props ) {
	useTranslations( translations )
	const { isFallback, replace } = useRouter()

	const { height, width } = useWindowSize()
	const quickView = false && height > 550 && width > 700

	useEffect( () => {
		if ( collection ) {
			cvgTrack({
				eventName: 'Viewed Collection',
				properties: parseCollection( collection ),
			})
		}
	}, [ collection ] )

	// Redirect to /products on 404
	useEffect( () => {
		if ( !isFallback && !collection ) replace( '/products' )
	}, [ isFallback, !collection ] )

	if ( isFallback ) return <Loading />
	if ( !isFallback && !collection ) {
		Sentry.captureMessage( `404: collection ${handle}` )
		return (
			<Error statusCode={404} title="This collection could not be found" />
		)
	}

	const title = i.t( [
		`collection.title.${collection.handle}`,
		'collection.title.generic',
	], { collection: getTitle( collection ) })
	const pageTitle = i.t( [
		`title.collection.${collection.handle}`,
		'title.generic',
	], { page: title, suffix: i.t( 'title.suffix' ) })

	const description = i.t(
		[ `title.description.${collection}`, 'title.description.generic' ]
	)

	const index = collections.findIndex( c => c.handle === collection.handle )
	const next = collections[ ( index + 1 ) % collections.length ]

	return (
		<Layout loaded={loaded} nav={nav} collections={collections}>
			<Head>
				<title>{pageTitle}</title>
				<meta property="og:title" content={pageTitle} />
				<meta name="description" content={description} />

				{i.exists( `og:image.collection-${collection.handle}` ) && (
					<>
						<meta name="twitter:card" content="summary_large_image" />
						<meta
							property="og:image"
							content={res(
								i.t( `og:image.collection-${collection.handle}` ),
								'1200x627_crop_center'
							)}
						/>
						<meta property="og:image:width" content="1200" />
						<meta property="og:image:height" content="627" />
					</>
				)}
			</Head>
			<header className="container">
				<H1 title={title} lines={1} />
			</header>

			<motion.main
				className={classnames( 'cf products', {
					five: collection.products.length >= 5,
					six: collection.products.length >= 6,
				})}
				transition={{ staggerChildren: .1, ease }}
				initial="initial"
				animate="animate"
				exit="exit"
				variants={{
					animate: {},
					exit: { opacity: 0, y: 80 },
				}}
			>
				{collection.products.map( product => (
					<Thumbnail
						key={product.handle}
						product={product}
						collection={collection.handle}
						quickView={quickView}
						quickAdd
					/>
				) )}
			</motion.main>
			{/* <TextImageFull
				align="left"
				image={i.t( [
					`collection.benefits.${collection.handle}.image`,
					'collection.benefits.generic.image',
				] )}
				text={i.t(
					[
						`collection.benefits.${collection.handle}`,
						'collection.benefits.generic',
					],
					{ postProcess: 'markdown-jsx' }
				)}
			/>
			<TextImageHalf
				align="right"
				image={i.t( [
					`collection.about.${collection.handle}.image`,
					'collection.about.generic.image',
				] )}
				text={i.t(
					[
						`collection.about.${collection.handle}`,
						'collection.about.generic',
					],
					{ postProcess: 'markdown-jsx' }
				)}
			/> */}
			<CollectionSection collection={next} />
			<Footer />

			<style jsx>{`
				header {
					margin-top: 60px;
					display: flex;
					justify-content: space-between;
					align-items: center;
					text-transform: lowercase;
				}
				header :global( h1 ) {
					margin: 0;
				}
				nav {
					font-size: 18px;
				}
				nav :global( ul ) {
					display: flex;
				}
				nav :global( li ) {
					position: relative;
					margin-left: 2em;
				}

				:global( main.products ) {
					margin-top: 60px;
					display: grid;
					grid-template-columns: 1fr 1fr 1fr;
					grid-template-rows: auto;
					grid-column-gap: 36px;
					grid-row-gap: 62px;
				}

				@media ( max-width: 900px ) {
					header {
						margin-top: 0px;
						flex-wrap: wrap;
					}
					nav {
						margin: 16px 0;
					}
					nav :global( ul ) {
						flex-wrap: wrap;
					}
					nav :global( li ) {
						margin-left: 0;
						margin-right: 2em;
						margin-block: 1em;
					}
					:global( main.products ) {
						margin-top: 50px;
						margin-bottom: 100px;
						grid-template-columns: 1fr 1fr;
						grid-column-gap: 4vw;
					}
					:global( main.products picture ) {
						padding: 60px 10px 40px;
					}
				}
				@media ( min-width: 301px ) and ( max-width: 900px ) {
					:global( main.products ) {
						padding: 0 4vw;
					}
					:global( main.products article h3 ),
					:global( main.products article p ) {
						font-size: 16px !important;
					}
				}
				@media ( min-width: 301px ) and ( max-width: 800px ) {
					:global( main.products picture ) {
						padding: calc( 25% + 20px ) 0 30%;
						height: 70vw;
					}
				}
				@media ( min-width: 301px ) and ( max-width: 450px ) {
					:global( main.products article .count ) {
						display: none;
					}
					:global( main.products article .count-short ) {
						display: revert;
					}
				}
				@media ( max-width: 300px ) {
					:global( main.products ) {
						grid-template-columns: 1fr;
					}
					:global( main.products picture ) {
						height: 110vw;
						padding: 30% 0;
					}
					nav {
						margin: 30px 0 -16px;
					}
				}
				@media ( min-width: 1350px ) {
					:global( main.products ) {
						grid-template-columns: 1fr 1fr 1fr 1fr;
					}
					:global( main.products picture ) {
						height: 30vw;
					}
				}
				@media ( min-width: 1700px ) {
					:global( main.products.five ) {
						grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
					}
					:global( main.products.five picture ) {
						height: 25vw;
					}
				}
				@media ( min-width: 2100px ) {
					:global( main.products.six ) {
						grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
					}
					:global( main.products.six picture ) {
						height: 22vw;
					}
				}
			`}</style>
		</Layout>
	)
}
